/**
 * v-tip — stylet tooltip-direktiv (session 30, [D-088] + 03-ui-design §12.5).
 *
 * Direkte erstatning for native `title=""`-tooltips, som er FORBUDT i UI'et
 * (usytlede, ~1s delay, bryder monochrome-designet). Brug:
 *
 *   <button v-tip="'Fold ud alle'">…</button>
 *   <span v-tip="dynamicTooltipText">…</span>
 *
 * Mekanik: singleton tooltip-div i <body> (position: fixed via
 * getBoundingClientRect → klippes aldrig af overflow-hidden/sticky-celler).
 * I FULD SKÆRM (Fullscreen API på ét element, fx ledelses-dækket) tegner
 * browseren kun det fuldskærms-element og dets efterkommere, så en tooltip i
 * <body> er usynlig; ved hver visning flyttes tooltip-elementet derfor ind i
 * `document.fullscreenElement` når der er ét, og tilbage til <body> ellers
 * (Charlie 26-10-2026: "tooltip vises ikke i fuldskærmsvisning"; samme greb
 * som konfetti-laget i lib/confetti.ts).
 * Vises på mouseenter/focus, fjernes på mouseleave/blur/unmount. Centreret
 * OVER elementet; flipper automatisk under hvis der ikke er plads opad.
 * Tom/null værdi = ingen tooltip. `\n` i indholdet giver linjeskift
 * (white-space: pre-line) — bruges af benchmark-rolling-tooltips.
 *
 * Ud over en streng accepteres `{ rows: [{ label, value }] }` — renderes som
 * to-kolonne-opstilling m. label venstrestillet og værdi HØJRESTILLET
 * (tabular-nums), det revisionsagtige udtryk til beløbs-tooltips
 * (Salgsstatistiks celle-tooltip, Charlie 17-07). Rækkerne bygges m.
 * createElement/textContent — aldrig innerHTML (ingen injektionsflade).
 *
 * Styling matcher HelpTip-fladen (bg-surface, border-line, rounded-lg,
 * shadow, text-[11px]) så alle tooltips i appen er ens.
 *
 * Revisionsopstillingen kan desuden bære ([D-338], Marketing → Månedstal):
 *   - `title`: en overskrift over tallene (fx "Omsætning, sep 2026"),
 *   - `head`: kolonnehoveder, når en række har flere værdier (fx
 *     ['', '2026', '2025', 'YoY']); en række får da `value` som et array,
 *   - alle labels står venstrestillet, også underposter som "AOV butik"
 *     (Charlie 29-09-2026: "venstrestille teksten i tooltippet, så fx AOV
 *     butik og AOV web, også er venstrestillet"); ingen indrykning,
 *   - `note`: en kort forklaring under tallene (fx hvad ~ betyder her).
 */

import type { Directive } from 'vue';

import { formatDurationFullDa } from '@cockpit/shared';

import { formatFullDateTimeDa } from '../lib/datetime';

const TIP_KEY = Symbol('v-tip');

/** Én linje i en revisions-tooltip: label venstre, værdi(er) højre. */
export interface TipRow {
  label: string;
  /** Én værdi, eller én pr. kolonne når tooltippen har `head`. */
  value: string | string[];
}
export type TipContent = string | { title?: string; head?: string[]; rows: TipRow[]; note?: string };

interface TipState {
  content: TipContent;
  /** Den fulde dato fra v-fulldate (tom = ingen). */
  fullDate: string;
  /** Den fulde varighed fra v-duration (tom = ingen). */
  fullDuration: string;
  onEnter: () => void;
  onLeave: () => void;
}
interface TipElement extends HTMLElement {
  [TIP_KEY]?: TipState;
}

/** Singleton tooltip-element — én pr. app, genbruges på tværs af triggers. */
let tipEl: HTMLDivElement | null = null;
let activeTrigger: HTMLElement | null = null;

function ensureTipEl(): HTMLDivElement {
  if (tipEl) return tipEl;
  tipEl = document.createElement('div');
  tipEl.setAttribute('role', 'tooltip');
  // Tailwind-klasser virker globalt; pre-line så \n giver linjeskift.
  tipEl.className =
    'fixed z-[100] px-3 py-2 bg-surface text-[12px] leading-relaxed text-ink ' +
    'border border-line rounded-lg shadow-[0_6px_18px_rgba(0,0,0,0.18)] ' +
    'pointer-events-none whitespace-pre-line max-w-[320px] w-max';
  tipEl.style.display = 'none';
  document.body.appendChild(tipEl);
  return tipEl;
}

/** Render indholdet i tooltip-elementet — tekst eller to-kolonne-rækker. */
function renderContent(el: HTMLDivElement, content: TipContent): void {
  el.textContent = '';
  el.style.maxWidth = '';
  if (typeof content === 'string') {
    el.textContent = content;
    // Lange forklaringer ([D-447]: sidernes fodnoter og intro-afsnit bor nu i
    // tooltips) får en bredere boks, så de ikke bliver en smal, meterhøj søjle.
    if (content.length > 260) el.style.maxWidth = content.length > 700 ? '560px' : '440px';
    return;
  }
  if (content.title) {
    const title = document.createElement('div');
    title.textContent = content.title;
    title.className = 'font-medium text-ink mb-1';
    el.appendChild(title);
  }
  const cols = Math.max(
    1,
    (content.head?.length ?? 1) - 1,
    ...content.rows.map((r) => (Array.isArray(r.value) ? r.value.length : 1)),
  );
  // Flere værdikolonner (2 perioder + ændring) kræver mere end 320 px.
  el.style.maxWidth = cols > 1 ? '520px' : '';
  const grid = document.createElement('div');
  grid.className = 'grid gap-x-4';
  grid.style.gridTemplateColumns = `auto repeat(${cols}, auto)`;
  if (content.head) {
    content.head.forEach((h, i) => {
      const cell = document.createElement('span');
      cell.textContent = h;
      cell.className = `text-[11px] text-faint border-b border-line pb-0.5 mb-0.5${i > 0 ? ' text-right' : ''}`;
      grid.append(cell);
    });
    for (let i = content.head.length; i < cols + 1; i++) grid.append(document.createElement('span'));
  }
  for (const row of content.rows) {
    const label = document.createElement('span');
    label.textContent = row.label;
    label.className = 'text-muted';
    grid.append(label);
    const values = Array.isArray(row.value) ? row.value : [row.value];
    for (let i = 0; i < cols; i++) {
      const value = document.createElement('span');
      value.textContent = values[i] ?? '';
      value.className = 'text-right tabular-nums';
      grid.append(value);
    }
  }
  el.appendChild(grid);
  if (content.note) {
    const note = document.createElement('div');
    note.textContent = content.note;
    note.className = 'mt-1.5 text-[11px] leading-snug text-faint';
    el.appendChild(note);
  }
}

function hasContent(content: TipContent): boolean {
  return typeof content === 'string' ? content !== '' : content.rows.length > 0;
}

/**
 * Det der vises: v-tip-indholdet med v-fulldate-datoen og v-duration-varigheden
 * ØVERST, når elementet bærer flere (fx "Opdaget"-cellen: datoen, og under den
 * hvor mange gange).
 */
function composed(state: TipState): TipContent {
  const { content } = state;
  const fullDate = [state.fullDate, state.fullDuration].filter(Boolean).join('\n');
  if (!fullDate) return content;
  if (typeof content === 'string') return content ? `${fullDate}
${content}` : fullDate;
  if (!content.title) return { ...content, title: fullDate };
  return { ...content, note: content.note ? `${content.note}
${fullDate}` : fullDate };
}

function show(trigger: TipElement): void {
  const state = trigger[TIP_KEY];
  if (!state) return;
  const content = composed(state);
  if (!hasContent(content)) return;
  const el = ensureTipEl();
  // Fuld skærm: kun fullscreen-elementets undertræ tegnes, så tooltippen skal bo dér.
  const host = document.fullscreenElement ?? document.body;
  if (el.parentElement !== host) host.appendChild(el);
  activeTrigger = trigger;
  renderContent(el, content);
  el.style.display = 'block';
  el.style.visibility = 'hidden';
  el.style.left = '0px';
  el.style.top = '0px';
  // Mål efter render, placér derefter (centreret over; flip under ved pladsmangel).
  requestAnimationFrame(() => {
    if (activeTrigger !== trigger || !tipEl) return;
    const r = trigger.getBoundingClientRect();
    const t = tipEl.getBoundingClientRect();
    const margin = 6;
    let left = r.left + r.width / 2 - t.width / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - t.width - 8));
    let top = r.top - t.height - margin;
    if (top < 8) top = r.bottom + margin; // flip under
    tipEl.style.left = `${Math.round(left)}px`;
    tipEl.style.top = `${Math.round(top)}px`;
    tipEl.style.visibility = 'visible';
  });
}

function hide(trigger: HTMLElement): void {
  if (activeTrigger !== trigger) return;
  activeTrigger = null;
  if (tipEl) {
    tipEl.style.display = 'none';
    tipEl.textContent = '';
  }
}

function normalize(value: unknown): TipContent {
  if (value == null) return '';
  if (typeof value === 'object' && Array.isArray((value as { rows?: unknown }).rows)) {
    const v = value as { rows: unknown[]; title?: unknown; head?: unknown; note?: unknown };
    const rows = v.rows.filter(
      (r): r is TipRow =>
        typeof r === 'object' && r !== null &&
        typeof (r as TipRow).label === 'string' &&
        (typeof (r as TipRow).value === 'string' ||
          (Array.isArray((r as TipRow).value) && ((r as TipRow).value as unknown[]).every((x) => typeof x === 'string'))),
    );
    return {
      rows,
      title: typeof v.title === 'string' ? v.title : undefined,
      head: Array.isArray(v.head) && v.head.every((x) => typeof x === 'string') ? (v.head as string[]) : undefined,
      note: typeof v.note === 'string' ? v.note : undefined,
    };
  }
  return String(value).trim();
}

/** Fælles tilstand for v-tip, v-fulldate og v-duration: én lytter-sæt pr. element. */
function attach(el: TipElement): TipState {
  const existing = el[TIP_KEY];
  if (existing) return existing;
  const state: TipState = {
    content: '',
    fullDate: '',
    fullDuration: '',
    onEnter: () => show(el),
    onLeave: () => hide(el),
  };
  el[TIP_KEY] = state;
  el.addEventListener('mouseenter', state.onEnter);
  el.addEventListener('mouseleave', state.onLeave);
  el.addEventListener('focus', state.onEnter);
  el.addEventListener('blur', state.onLeave);
  // Fjern evt. native title så browser-tooltip aldrig dobbelt-vises.
  if (el.hasAttribute('title')) el.removeAttribute('title');
  return state;
}

/** Opdatér live hvis tooltippen er åben på dette element. */
function refresh(el: TipElement, state: TipState): void {
  if (activeTrigger !== el) return;
  const content = composed(state);
  if (!hasContent(content)) hide(el);
  else if (tipEl) renderContent(tipEl, content);
}

function detach(el: TipElement): void {
  const state = el[TIP_KEY];
  if (state) {
    el.removeEventListener('mouseenter', state.onEnter);
    el.removeEventListener('mouseleave', state.onLeave);
    el.removeEventListener('focus', state.onEnter);
    el.removeEventListener('blur', state.onLeave);
    delete el[TIP_KEY];
  }
  hide(el);
}

/** v-tip direktivet. Værdi: string | { rows } | null/undefined (tom = ingen tooltip). */
export const vTip: Directive<TipElement, unknown> = {
  mounted(el, binding) {
    attach(el).content = normalize(binding.value);
  },
  updated(el, binding) {
    const state = el[TIP_KEY];
    if (!state) return;
    state.content = normalize(binding.value);
    refresh(el, state);
  },
  unmounted: detach,
};

/**
 * v-fulldate (Charlie 23-10-2026, 12-conventions §94): sættes på ethvert
 * element der kan vise en relativ dato ("I dag", "I går", "3 timer siden"), med
 * det tidspunkt datoen står for. Tooltippen viser så den fulde ugedag, dato og
 * klokkeslæt ("Torsdag 22. oktober 2026 kl. 14:53"):
 *
 *   <td v-fulldate="r.firstSeenAt">{{ formatEpochDateTimeDa(r.firstSeenAt) }}</td>
 *
 * Værdi: epoch-ms, Date, ISO-tidspunkt eller ren dato ('YYYY-MM-DD'; så uden
 * klokkeslæt). null/undefined = ingen tooltip. Har elementet også v-tip, står
 * datoen øverst i samme tooltip. Web-buildet afviser en relativ dato i en
 * template uden direktivet (scripts/check-relative-dates.ts).
 */
export const vFullDate: Directive<TipElement, unknown> = {
  mounted(el, binding) {
    attach(el).fullDate = toFullDate(binding.value);
  },
  updated(el, binding) {
    const state = el[TIP_KEY];
    if (!state) return;
    state.fullDate = toFullDate(binding.value);
    refresh(el, state);
  },
  unmounted: detach,
};

function toFullDate(value: unknown): string {
  if (typeof value === 'number' || typeof value === 'string' || value instanceof Date) {
    return formatFullDateTimeDa(value);
  }
  return '';
}

/**
 * v-duration (Charlie 02-11-2026, 12-conventions §100, [D-523]): sættes på
 * ethvert element der viser en varighed i kort form ("43 m", "15 mdr", fra
 * formatDurationDa), med varigheden i ms. Tooltippen viser den fulde varighed,
 * udspecificeret ("43 minutter og 3 sekunder"):
 *
 *   <td v-duration="r.durationMs">{{ formatDurationDa(r.durationMs) }}</td>
 *
 * null/undefined = ingen tooltip. Har elementet også v-tip, står varigheden
 * øverst i samme tooltip. Web-buildet afviser en kort varighed i en template
 * uden direktivet (scripts/check-relative-dates.ts).
 */
export const vDuration: Directive<TipElement, unknown> = {
  mounted(el, binding) {
    attach(el).fullDuration = toFullDuration(binding.value);
  },
  updated(el, binding) {
    const state = el[TIP_KEY];
    if (!state) return;
    state.fullDuration = toFullDuration(binding.value);
    refresh(el, state);
  },
  unmounted: detach,
};

function toFullDuration(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) ? formatDurationFullDa(value) : '';
}
